/* global Office console */

import { RecurrenceInfo } from "../taskpane/domain/RecurrenceInfo";
import { Room } from "../taskpane/domain/Room";

function resolveAsyncDate(
  result: Office.AsyncResult<Date>,
  propertyName: string,
  resolve: (value: Date) => void,
  reject: (reason?: any) => void
) {
  if (result.status === Office.AsyncResultStatus.Succeeded) {
    console.log(`Item ${propertyName}: ${result.value}`);
    resolve(result.value);
  } else {
    console.error(`Get item ${propertyName} failed: ${result.error.message}`);
    reject(result.error.message);
  }
}

export async function getAppointmentStart(): Promise<Date> {
  return new Promise<Date>((resolve, reject) => {
    if (Office.context.mailbox.item) {
      Office.context.mailbox.item.start.getAsync((result) => resolveAsyncDate(result, "start", resolve, reject));
    } else {
      reject("Item not available to get start");
    }
  });
}

export async function getAppointmentEnd(): Promise<Date> {
  return new Promise<Date>((resolve, reject) => {
    if (Office.context.mailbox.item) {
      Office.context.mailbox.item.end.getAsync((result) => resolveAsyncDate(result, "end", resolve, reject));
    } else {
      reject("Item not available to get end");
    }
  });
}

export async function getRecurrenceInfo(): Promise<RecurrenceInfo | null> {
  const recurrenceInfoPromise = new Promise<RecurrenceInfo | null>((resolve, reject) => {
    if (Office.context.mailbox.item) {
      if (Office.context.mailbox.item.seriesId) {
        console.log("Series ID found - item is an occurrence");
        resolve(null);
      } else {
        Office.context.mailbox.item.recurrence.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            if (result.value === null) {
              console.log("Item is not recurring.");
              resolve(null);
            } else {
              console.log(`Recurrence pattern: ${JSON.stringify(result.value)}`);
              resolve(new RecurrenceInfo(result.value));
            }
          } else {
            console.error(`Get item recurrence failed: ${result.error.message}`);
            // continue without recurrence info
            resolve(null);
          }
        });
      }
    } else {
      reject("Item not available to get recurrence info");
    }
  });
  return recurrenceInfoPromise;
}

function extractLocationIdentifiers(locationDetails: Office.LocationDetails[]): Office.LocationIdentifier[] {
  const locationIdentifiers: Office.LocationIdentifier[] = [];
  if (locationDetails != null) {
    locationDetails.forEach((item: Office.LocationDetails) => {
      locationIdentifiers.push(item.locationIdentifier);
    });
  }
  return locationIdentifiers;
}

const isSameEmail = (email1: string, email2: string): boolean => {
  return email1.localeCompare(email2, undefined, { sensitivity: "accent" }) == 0;
};

function extractRoomEmails(currentLocations: Office.LocationIdentifier[], roomToExclude: Room): string[] {
  const roomEmails: string[] = [];
  currentLocations.forEach((item: Office.LocationIdentifier) => {
    if (item.type === Office.MailboxEnums.LocationType.Room && !isSameEmail(item.id, roomToExclude.mailbox_id)) {
      roomEmails.push(item.id);
    }
  });
  return roomEmails;
}

function filterLocationsToRemove(
  currentLocations: Office.LocationIdentifier[],
  room: Room,
  emailsToRemove: string[]
): Office.LocationIdentifier[] {
  return currentLocations.filter((item) => {
    let remove = true;
    if (item.type === Office.MailboxEnums.LocationType.Room) {
      remove = !isSameEmail(item.id, room.mailbox_id) && emailsToRemove.some((email) => isSameEmail(email, item.id));
    } else if (room.location) {
      remove = item.id !== room.location;
    }
    return remove;
  });
}

function buildLocationsToAdd(currentLocations: Office.LocationIdentifier[], room: Room): Office.LocationIdentifier[] {
  const locationsToAdd: Office.LocationIdentifier[] = [];
  if (
    currentLocations.every(
      (item) => item.type !== Office.MailboxEnums.LocationType.Room || !isSameEmail(item.id, room.mailbox_id)
    )
  ) {
    locationsToAdd.push({
      id: room.mailbox_id,
      type: Office.MailboxEnums.LocationType.Room,
    });
  }

  if (
    room.location &&
    currentLocations.every((item) => item.type !== Office.MailboxEnums.LocationType.Custom || item.id !== room.location)
  ) {
    locationsToAdd.push({
      id: room.location,
      type: Office.MailboxEnums.LocationType.Custom,
    });
  }

  return locationsToAdd;
}

function addLocations(locationsToAdd: Office.LocationIdentifier[]) {
  if (Office.context.mailbox.item) {
    Office.context.mailbox.item.enhancedLocation.addAsync(locationsToAdd, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error(`Add locations failed: ${result.error.message}`);
      } else {
        console.log("Add locations succeeded");
      }
    });
  } else {
    console.log("Item not available to add locations");
  }
}

function addRoomIfMissing(currentLocations: Office.LocationIdentifier[], room: Room) {
  const locationsToAdd = buildLocationsToAdd(currentLocations, room);
  if (locationsToAdd.length > 0) {
    addLocations(locationsToAdd);
  } else {
    console.log("Current locations already contain the room");
  }
}

function removeLocations(locationsToRemove: Office.LocationIdentifier[]) {
  if (Office.context.mailbox.item) {
    Office.context.mailbox.item.enhancedLocation.removeAsync(locationsToRemove, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error(`Remove locations failed: ${result.error.message}`);
      } else {
        console.log("Remove locations succeeded");
      }
    });
  } else {
    console.log("Item not available to remove locations");
  }
}

function removeOtherRooms(currentLocations: Office.LocationIdentifier[], room: Room, emailsToRemove: string[]) {
  const locationsToRemove = filterLocationsToRemove(currentLocations, room, emailsToRemove);
  if (locationsToRemove.length > 0) {
    removeLocations(locationsToRemove);
  } else {
    console.log("No locations need to be removed");
  }
}

export function setLocation(room: Room, roomEmailFilter: (emails: string[]) => Promise<string[]>) {
  if (Office.context.mailbox.item) {
    Office.context.mailbox.item.enhancedLocation.getAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error(`Get current locations failed: ${result.error.message}`);
      } else {
        console.log(`Current locations: ${result.value}`);
        const currentLocations = extractLocationIdentifiers(result.value);
        // extract all room emails except the room to set
        const otherEmails = extractRoomEmails(currentLocations, room);
        roomEmailFilter(otherEmails)
          .then((emailsToRemove) => {
            removeOtherRooms(currentLocations, room, emailsToRemove);
          })
          .catch((reason: any) => {
            console.log("Failed to determine locations to remove");
            console.log(reason);
          })
          // initiate adding the room only after the filter completes
          // to avoid having both the old and new room in the meeting
          .finally(() => {
            addRoomIfMissing(currentLocations, room);
          });
        // TODO consider removing other rooms from required and optional attendees
        // For required and optional attendees, don't skip the room to set.
        // Skip distribution groups? Current user could be external!
      }
    });
  } else {
    console.log("Item not available to set location");
  }
}

export async function getLocationEmails(): Promise<string[]> {
  return new Promise<string[]>((resolve, reject) => {
    if (Office?.context?.mailbox?.item) {
      Office.context.mailbox.item.enhancedLocation.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log(`Locations in item: ${result.value}`);
          const currentLocations = extractLocationIdentifiers(result.value);
          // extract all room emails by passing a room without email
          const roomEmails = extractRoomEmails(currentLocations, new Room());
          // proceed with the extracted emails
          resolve(roomEmails);
        } else {
          console.error(`Get locations failed: ${result.error.message}`);
          reject(result.error.message);
        }
      });
    } else {
      reject("Item not available to get locations");
    }
  });
}

function extractAttendeeEmails(
  result: Office.AsyncResult<Office.EmailAddressDetails[]>,
  propertyName: string,
  resolve: (value: string[]) => void,
  reject: (reason?: any) => void
) {
  if (result.status === Office.AsyncResultStatus.Succeeded) {
    console.log(`Got ${result.value.length} ${propertyName} attendees`);
    const emails: string[] = [];
    result.value.forEach((item: Office.EmailAddressDetails) => {
      emails.push(item.emailAddress);
    });
    // proceed with the extracted emails
    resolve(emails);
  } else {
    console.log(`Get ${propertyName} attendees failed: ${result.error.message}`);
    reject(result.error.message);
  }
}

export async function getRequiredAttendeeEmails(): Promise<string[]> {
  return new Promise<string[]>((resolve, reject) => {
    if (Office.context.mailbox.item) {
      Office.context.mailbox.item.requiredAttendees.getAsync((result) =>
        extractAttendeeEmails(result, "required", resolve, reject)
      );
    } else {
      reject("Item not available to get required attendees");
    }
  });
}

export async function getOptionalAttendeeEmails(): Promise<string[]> {
  return new Promise<string[]>((resolve, reject) => {
    if (Office.context.mailbox.item) {
      Office.context.mailbox.item.optionalAttendees.getAsync((result) =>
        extractAttendeeEmails(result, "optional", resolve, reject)
      );
    } else {
      reject("Item not available to get optional attendees");
    }
  });
}

export async function getAttendeeEmails(): Promise<string[]> {
  return Promise.all([getRequiredAttendeeEmails(), getOptionalAttendeeEmails()]).then((values: [string[], string[]]) =>
    values[0].concat(values[1])
  );
}
